import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, MapPin } from 'lucide-react';
import { VCardData, VCardAddress } from '@/lib/vcard-utils';
import { countries } from '@/lib/phone-utils';

interface VCardAddressesProps {
  data: VCardData;
  onChange: (data: Partial<VCardData>) => void;
}

const addressTypes = [
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
];

export function VCardAddresses({ data, onChange }: VCardAddressesProps) {
  const addAddress = () => {
    if (data.addresses.length >= 3) return;
    const newAddress: VCardAddress = {
      id: Date.now().toString(),
      type: 'work',
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    };
    onChange({ addresses: [...data.addresses, newAddress] });
  };

  const updateAddress = (id: string, updates: Partial<VCardAddress>) => {
    onChange({
      addresses: data.addresses.map((a) =>
        a.id === id ? { ...a, ...updates } : a
      ),
    });
  };

  const removeAddress = (id: string) => {
    onChange({ addresses: data.addresses.filter((a) => a.id !== id) });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Addresses</Label>
        <span className="text-xs text-muted-foreground">{data.addresses.length}/3</span>
      </div>

      {data.addresses.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <MapPin className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No addresses added yet</p>
          <Button type="button" variant="outline" size="sm" onClick={addAddress}>
            <Plus className="h-4 w-4 mr-2" />
            Add Address
          </Button>
        </div>
      )}

      {data.addresses.map((address, index) => (
        <div
          key={address.id}
          className="p-4 border rounded-lg space-y-4 bg-muted/20"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select
                value={address.type}
                onValueChange={(value) => updateAddress(address.id, { type: value as VCardAddress['type'] })}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {addressTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">Address #{index + 1}</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeAddress(address.id)}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor={`street1-${address.id}`}>Street Address</Label>
              <Input
                id={`street1-${address.id}`}
                value={address.street1}
                onChange={(e) => updateAddress(address.id, { street1: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`street2-${address.id}`}>Address Line 2</Label>
              <Input
                id={`street2-${address.id}`}
                value={address.street2}
                onChange={(e) => updateAddress(address.id, { street2: e.target.value })}
                placeholder="Apt, Suite, Building (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor={`city-${address.id}`}>City</Label>
                <Input
                  id={`city-${address.id}`}
                  value={address.city}
                  onChange={(e) => updateAddress(address.id, { city: e.target.value })}
                  placeholder="New York"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`state-${address.id}`}>State/Province</Label>
                <Input
                  id={`state-${address.id}`}
                  value={address.state}
                  onChange={(e) => updateAddress(address.id, { state: e.target.value })}
                  placeholder="NY"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor={`postal-${address.id}`}>Postal/ZIP Code</Label>
                <Input
                  id={`postal-${address.id}`}
                  value={address.postalCode}
                  onChange={(e) => updateAddress(address.id, { postalCode: e.target.value })}
                  placeholder="10001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`country-${address.id}`}>Country</Label>
                <Select
                  value={address.country}
                  onValueChange={(value) => updateAddress(address.id, { country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      ))}

      {data.addresses.length > 0 && data.addresses.length < 3 && (
        <Button type="button" variant="outline" size="sm" onClick={addAddress} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Another Address
        </Button>
      )}
    </div>
  );
}
